import React from "react"
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import { SearchBar } from "../../components/Tables/Columns"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"

function ReadyBagsTable({
    data,
    setSelectedRows,
    selectedRows
}: {
    data: Record<string, any>[]
    setSelectedRows: Function
    selectedRows: string[]
}) {
    const { t } = useTranslation()

    const onSelect = (row: Record<string, any>, isSelect: any) => {
        if (isSelect) {
            if (selectedRows.some((id) => `${id}` === `${row.id}`)) return
            setSelectedRows([...selectedRows, row.id])
        } else {
            setSelectedRows(selectedRows?.filter((sp) => `${sp}` !== `${row?.id}`))
        }
    }

    const handleSelectAll = (isSelect: any, rows: Record<string, any>[]) => {
        if (isSelect) {
            setSelectedRows(rows?.map((row) => row?.id))
        } else {
            setSelectedRows([])
        }
    }

    const selectRow: any = {
        mode: "checkbox",
        clickToSelect: false,
        onSelect: onSelect,
        onSelectAll: handleSelectAll,
        selectColumnPosition: "left",
        hideSelectAll: true,
        selected: selectedRows
    }

    const columns: ColumnDescription<any, any>[] = [
        {
            text: t("Bag ID"),
            dataField: "bag_id",
            classes: "highlighted-text"
        },
        {
            text: t("Date"),
            dataField: "created_at",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                let date
                if (row && cell?.length > 0) {
                    date = dateFormatter(row?.created_at, false, "")
                }
                return cell && cell?.length > 0 ? (
                    <>
                        <span className="d-block">{date}</span>
                    </>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Parcels"),
            dataField: "parcels_count",
            formatter: (cell, row) => {
                return cell ?? row.parcels?.length ?? 0
            }
        },
        {
            text: t("Type"),
            dataField: "bag_type",
            formatter: (cell, row) =>
                (row.bag_type ?? "").toLowerCase() === "transit" ? (
                    <div className="d-flex flex-row flex-nowrap align-items-center">
                        {cell}
                        <Flag
                            className="d-inline-block mx-1"
                            style={{ width: 28, height: 18 }}
                            code={row.final_destination ?? ""}
                        />
                        {row.final_destination}
                    </div>
                ) : (
                    (cell ?? "-")
                )
        },
        {
            text: t("Clearance Agent Details"),
            dataField: "agent_detail",
            formatter: (cell) => cell ?? "-"
        },
        {
            text: t("Dims"),
            dataField: "dims",
            formatter: (cell, row) => {
                return `${row?.height || "-"} x ${row?.width || "-"} x ${
                    row?.length || "-"
                } ${row?.dimensions_unit || "inch|cm"}`
            }
        },
        {
            text: t("A. Weight"),
            dataField: "a-weight",
            formatter: (cell: any, row: Record<string, any>) => {
                return `${row?.actual_weight_value || "-"} ${row?.weight_unit || "lb|kg"}`
            }
        },
        {
            text: t("Destination"),
            dataField: "destination",
            formatter: (cell, row) => {
                return typeof cell === "string" && cell !== "" && countryNamesInEnglish.of(cell) ? (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>}>
                        <>
                            <Flag
                                className="d-inline-block mx-1"
                                style={{ width: 28, height: 18 }}
                                code={(cell ?? "").toLowerCase()}
                            />
                            &nbsp;{cell}
                        </>
                    </OverlayTrigger>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Value"),
            dataField: "bag_invoice_value",
            formatter: (cell, row) => {
                return typeof cell === "number" && cell > 0 ? `$${cell}` : "-"
            }
        }
    ]

    return (
        <div className="table-responsive">
            {columns && (
                <ToolkitProvider keyField="id" data={data} columns={columns} search={{ searchFormatted: true }}>
                    {(props) => (
                        <div className="table-container my-4 container-fluid">
                            <Row className="search suites-search">
                                <Col
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    className="d-flex justify-content-end px-0">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                            </Row>
                            <Row className="table-data my-3 modal-gray-bg">
                                <Col>
                                    <div
                                        style={{
                                            maxHeight: "200px",
                                            overflow: "auto"
                                        }}>
                                        <BootstrapTable
                                            {...props.baseProps}
                                            selectRow={selectRow}
                                            wrapperClasses="table-wrapper my-3 "
                                            headerClasses="header-class font-13"
                                            bordered={false}
                                            noDataIndication={t("No_Record")}
                                            filter={filterFactory()}
                                            rowClasses="custom-table-row"
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default React.memo(ReadyBagsTable)
